import React, { forwardRef, useImperativeHandle } from 'react';
import './LocaleForm.css';
import {
  Button,
  Dropdown,
  FlexGrid,
  getClassNameFactory,
  InlineErrorMessage,
  Responsive,
} from '@dcl/ui';
import { ILocaleForm, ILocaleFormRef } from './types';
import { filterFn, filterProduct, constructLocaleUrl } from './helper';
import {
  ACCOUNTINGCS,
  CLEAR_BUTTON_LABEL,
  DESKTOP_MIN_WIDTH,
  MOBILE_MAX_WIDTH,
  ULTRATAX,
} from './constant';
import { ButtonColor, ButtonKind } from '@dcl/ui/dist/elements/Button';
import ReactHtmlParser from 'react-html-parser';

export const LocaleForm = forwardRef<ILocaleFormRef, ILocaleForm>(
  (props, ref) => {
    const {
      localityButtonLabel,
      localityDropdownLabel,
      localityDropdownData,
      localityPath,
      payrollType,
      localityDefaultSelectionLabel,
      onLocaleFormRender,
      onClearLocaleForm,
      selectLocalForm,
    } = props;
    const getClassName = getClassNameFactory(LocaleForm.displayName);
    const [selectedState, setSelectedState] = React.useState(selectLocalForm);
    const [showError, setShowError] = React.useState(false);
    const [localData, setLocalData] = React.useState<any>();

    const dropdownItems = localityDropdownData.map((item) => ({
      label: item.localTitle,
      value: item.localValue,
    }));

    const handleFilterValueChange = (value: string) => {
      if (
        dropdownItems.find(
          (state) =>
            state.label.toLocaleLowerCase() === value.toLocaleLowerCase()
        )
      ) {
        setSelectedState(filterProduct(value, dropdownItems));
      } else {
        setSelectedState('');
      }
    };
    const urlWithParams = new URL(window.location.href);
    const handleClearButtonClick = () => {
      setSelectedState('');
      setLocalData('');

      urlWithParams.searchParams.delete('hs_localforms');
      window.history.pushState(null, '', urlWithParams.toString());
    };

    const handleLocaleClearButtonClick = () => {
      setSelectedState('');
      setLocalData('');
      onClearLocaleForm();

      urlWithParams.searchParams.delete('hs_localforms');
      window.history.pushState(null, '', urlWithParams.toString());
    };

    useImperativeHandle(ref, () => ({
      tabSwitchRef: triggerLinkChange,
    }));

    const handleValueChange = (value: string) => {
      setSelectedState(filterProduct(value, dropdownItems));
      setShowError(false);
      const urlWithParams = new URL(window.location.href);
      urlWithParams.searchParams.delete('hs_localforms');
      window.history.pushState(null, '', urlWithParams.toString());
    };

    const handleStateApi = () => {
      if (selectedState === '') {
        setShowError(true);
        return;
      }

      const urlWithParams = new URL(window.location.href);
      if (urlWithParams.searchParams.has('hs_localforms')) {
        urlWithParams.searchParams.set('hs_localforms', selectedState);
      } else {
        urlWithParams.searchParams.append('hs_localforms', selectedState);
      }
      window.history.pushState(null, '', urlWithParams.toString());

      const url = constructLocaleUrl(localityPath, selectedState);

      fetch(url)
        .then((response) => response.json())
        .then((data) => setLocalData(data));
    };

    React.useEffect(() => {
      const url = constructLocaleUrl(localityPath, selectedState);

      selectedState !== '' &&
        fetch(url)
          .then((response) => response.json())
          .then((data) => setLocalData(data));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLocaleValueChange = (value: string) => {
      const filteredState = filterProduct(value, dropdownItems);
      setSelectedState(filteredState);
      setShowError(false);

      if (filteredState) {
        const urlWithParams = new URL(window.location.href);
        if (urlWithParams.searchParams.has('hs_localforms')) {
          urlWithParams.searchParams.set('hs_localforms', filteredState);
        } else {
          urlWithParams.searchParams.append('hs_localforms', filteredState);
        }
        window.history.pushState(null, '', urlWithParams.toString());

        const url = constructLocaleUrl(localityPath, filteredState);

        fetch(url)
          .then((response) => response.json())
          .then((data) => {
            setLocalData(data);
            onLocaleFormRender();
          });
      }
    };

    const triggerLinkChange = () => {
      payrollType === ACCOUNTINGCS
        ? handleValueChange(selectedState)
        : handleLocaleValueChange(selectedState);
    };

    return (
      <div className={getClassName()}>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col
              xs={12}
              lg={12}
              md={12}
              className={`${getClassName({
                descendantName: 'searchDropdownDiv',
              })} ${
                payrollType === ULTRATAX
                  ? getClassName({ descendantName: 'ultrataxDropdownDiv' })
                  : ''
              }`}
            >
              <FlexGrid.Row>
                <FlexGrid.Col xs={12} lg={12} md={12}>
                  <label
                    className={getClassName({
                      descendantName: 'searchDropdownLabel',
                    })}
                  >
                    {localityDropdownLabel}
                  </label>
                </FlexGrid.Col>
                <FlexGrid.Col
                  xs={12}
                  lg={4}
                  md={4}
                  className={getClassName({ descendantName: 'searchDropdown' })}
                >
                  <Dropdown
                    filterFunction={filterFn}
                    typeaheadMode
                    onFilterValueChanged={(value) =>
                      handleFilterValueChange(value as string)
                    }
                    clearButtonLabel={CLEAR_BUTTON_LABEL}
                    onClearButtonClick={
                      payrollType === ACCOUNTINGCS
                        ? handleClearButtonClick
                        : handleLocaleClearButtonClick
                    }
                    maxVisibleItems={5}
                    block={true}
                    items={dropdownItems}
                    labeledBy={localityButtonLabel}
                    placeholder={
                      payrollType === ACCOUNTINGCS
                        ? ' '
                        : localityDefaultSelectionLabel
                    }
                    onValueChanged={(value) => {
                      payrollType === ACCOUNTINGCS
                        ? handleValueChange(value as string)
                        : handleLocaleValueChange(value as string);
                    }}
                    className={getClassName({
                      descendantName: 'customDropdownStyle',
                    })}
                    removeAutoFocus
                    value={
                      dropdownItems.find((item) => item.value === selectedState)
                        ?.label
                    }
                  />
                  {showError && payrollType === ACCOUNTINGCS && (
                    <InlineErrorMessage
                      errorMessage={localityDropdownLabel}
                      id="validationError"
                      className={getClassName({
                        descendantName: 'localError',
                        modifiers: showError ? 'visible' : 'hidden',
                      })}
                    />
                  )}
                </FlexGrid.Col>
                {payrollType === ACCOUNTINGCS && (
                  <FlexGrid.Col
                    xs={12}
                    md={12}
                    lg={8}
                    className={`${getClassName({
                      descendantName: 'searchStateButtonDiv',
                    })} ${
                      showError
                        ? getClassName({
                            descendantName: 'searchStateButtonDivError',
                          })
                        : ''
                    }`}
                  >
                    <Responsive minWidth={DESKTOP_MIN_WIDTH}>
                      <Button
                        color={ButtonColor.PRIMARY}
                        kind={ButtonKind.BUTTON}
                        disabled={false}
                        data-testid="submitLocaleButton"
                        onClick={handleStateApi}
                      >
                        {localityButtonLabel}
                      </Button>
                    </Responsive>
                    <Responsive minWidth={0} maxWidth={MOBILE_MAX_WIDTH}>
                      <Button
                        kind={ButtonKind.BUTTON}
                        color={ButtonColor.PRIMARY}
                        disabled={false}
                        data-testid="submiLocaleButton"
                        onClick={handleStateApi}
                        block={true}
                      >
                        {localityButtonLabel}
                      </Button>
                    </Responsive>
                  </FlexGrid.Col>
                )}
              </FlexGrid.Row>
            </FlexGrid.Col>

            <FlexGrid.Col
              xs={12}
              lg={12}
              md={12}
              className={getClassName({ descendantName: 'payrollData' })}
            >
              <div>{localData && ReactHtmlParser(localData.topicdata)}</div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
      </div>
    );
  }
);

LocaleForm.displayName = 'LocaleForm';
